import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  ChatIntegration,
  ChatRecentMessage,
  NoticeDraftSet,
} from '@cornerstone/shared';
import { API_BASE } from '../../client/config';
import { PageHeader } from '../../nav/Breadcrumb';
import { ConfirmModal, InfoTooltip, ModalShell } from '../../components/menu';
import { SearchInput } from '../../components/ui';
import { describeApiError, toast } from '../../components/toast';
import { cn } from '../../utils/cn';
import { H9NoticesSkeleton } from '../../components/skeleton/layouts/H9NoticesSkeleton';

// =============================================================================
// H9 > 공지사항 관리 (Figma) — Chat 수신 공지 테이블.
//   필터(스페이스/상태) + 검색(스페이스·공지 제목) + 테이블:
//   No. | 스페이스 | 공지 제목(→상세 팝업) | 활성 여부(토글·ⓘ) | 생성 시각 | 상태
//   | 재전송(승인 완료 Disabled) | 삭제(활성화 Disabled)
// 상태 도출(기존 noticeStatus 모델):
//   parsed null → 오류 · pending → 승인 대기 · confirmed/disabled → 승인 완료
//   (활성 여부 = confirmed ↔ disabled 토글) · deleted → 목록 제외.
//   승인 거부는 현행 파이프라인에 상태가 없어 필터 옵션만 존재(해당 행 없음).
// =============================================================================

type NoticeStatusLabel = '승인 완료' | '승인 거부' | '승인 대기' | '오류';

const PAGE_SIZE = 10;

const ACTIVE_TOOLTIP = '승인 거부, 승인 대기, 오류 상태의 공지사항은 활성화시킬 수 없습니다.';

function statusOf(m: ChatRecentMessage): NoticeStatusLabel {
  if (!m.parsed) return '오류';
  if (m.noticeStatus === 'pending') return '승인 대기';
  return '승인 완료'; // confirmed | disabled — 승인 자체는 완료
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}. ${p(d.getMonth() + 1)}. ${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

const STATUS_BADGE: Record<NoticeStatusLabel, string> = {
  '승인 완료': 'bg-accent text-white',
  '승인 거부': 'bg-[#3a3a3a] text-white',
  '승인 대기': 'bg-panel-3 text-text-dim',
  오류: 'bg-red text-white',
};

export function H9Notices(): JSX.Element {
  const [integrations, setIntegrations] = useState<ChatIntegration[]>([]);
  const [items, setItems] = useState<ChatRecentMessage[]>([]);
  const [spaceFilter, setSpaceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | NoticeStatusLabel>('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [err, setErr] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [detailTarget, setDetailTarget] = useState<ChatRecentMessage | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ChatRecentMessage | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [ir, nr] = await Promise.all([
        fetch(`${API_BASE}/api/integrations/chat`),
        fetch(`${API_BASE}/api/integrations/chat/notices?limit=300`),
      ]);
      if (ir.ok) setIntegrations((await ir.json()) as ChatIntegration[]);
      if (!nr.ok) throw new Error(`HTTP ${nr.status}`);
      const body = (await nr.json()) as { items?: ChatRecentMessage[] } | ChatRecentMessage[];
      setItems(Array.isArray(body) ? body : body.items ?? []);
      setErr(null);
    } catch (e) {
      setErr(describeApiError(e));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const labelFor = useCallback(
    (integrationId: string): string =>
      integrations.find((i) => i.id === integrationId)?.label ?? integrationId,
    [integrations],
  );

  const titleOf = (m: ChatRecentMessage): string =>
    m.parsed?.header ?? m.messageText.split('\n')[0] ?? '(제목 없음)';

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items
      .filter((m) => m.noticeStatus !== 'deleted')
      .filter((m) => {
        if (spaceFilter && m.integrationId !== spaceFilter) return false;
        if (statusFilter && statusOf(m) !== statusFilter) return false;
        if (q) {
          const hay = `${labelFor(m.integrationId)} ${titleOf(m)}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      });
  }, [items, spaceFilter, statusFilter, query, labelFor]);

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const paged = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const keyOf = (m: ChatRecentMessage) => `${m.integrationId}:${m.messageId}`;

  async function setNoticeStatus(
    m: ChatRecentMessage,
    status: 'confirmed' | 'disabled' | 'deleted',
  ): Promise<void> {
    const r = await fetch(
      `${API_BASE}/api/integrations/chat/notices/${encodeURIComponent(m.integrationId)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messageId: m.messageId, status }),
      },
    );
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
  }

  // 활성 여부 토글 — confirmed ↔ disabled. 승인 완료 상태에서만 가능(Figma).
  async function toggleActive(m: ChatRecentMessage): Promise<void> {
    const key = keyOf(m);
    if (busyKey) return;
    setBusyKey(key);
    try {
      await setNoticeStatus(m, m.noticeStatus === 'confirmed' ? 'disabled' : 'confirmed');
      await load();
    } catch (e) {
      toast(describeApiError(e), 'error');
    } finally {
      setBusyKey(null);
    }
  }

  // 재전송 — 초안 세트를 승인 스페이스로 다시 게시. 승인 완료는 Disabled(Figma).
  async function resend(m: ChatRecentMessage): Promise<void> {
    const key = keyOf(m);
    if (busyKey) return;
    setBusyKey(key);
    try {
      const sr = await fetch(
        `${API_BASE}/api/integrations/chat/draft-sets/by-source?integrationId=${encodeURIComponent(m.integrationId)}&messageId=${encodeURIComponent(m.messageId)}`,
      );
      if (sr.status === 404) {
        toast('재전송할 초안이 없습니다. 앱 연동 설정에서 재파싱 후 시도해 주세요.', 'error');
        return;
      }
      if (!sr.ok) throw new Error(`HTTP ${sr.status}`);
      const set = (await sr.json()) as NoticeDraftSet;
      const rr = await fetch(
        `${API_BASE}/api/integrations/chat/draft-sets/${encodeURIComponent(set.id)}/resend-approval`,
        { method: 'POST' },
      );
      if (!rr.ok) {
        // 서버가 내려준 안내 메시지(예: 봇 미가입)를 토스트에 그대로 노출.
        const body = (await rr.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? `HTTP ${rr.status}`);
      }
      toast('승인 스페이스로 재전송되었습니다.', 'success');
    } catch (e) {
      toast(describeApiError(e), 'error');
    } finally {
      setBusyKey(null);
    }
  }

  const btnCls =
    'inline-flex items-center px-3 py-1.5 text-[13px] border border-line rounded hover:border-line-strong hover:bg-panel disabled:opacity-40 disabled:cursor-not-allowed';

  if (isLoading) {
    return <H9NoticesSkeleton />;
  }

  return (
    <div className="h-full overflow-auto p-6">
      <PageHeader items={[{ label: 'H9' }, { label: '공지사항' }]} />

      {/* 필터 — 스페이스 / 상태 */}
      <div className="flex items-end gap-4 mb-4">
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-text-dim">스페이스</span>
          <select
            value={spaceFilter}
            onChange={(e) => {
              setSpaceFilter(e.target.value);
              setPage(1);
            }}
            className="w-[200px] px-2.5 py-2 bg-panel-2 border border-line rounded text-[14px] text-text"
          >
            <option value="">전체</option>
            {integrations.map((i) => (
              <option key={i.id} value={i.id}>{i.label}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-text-dim">상태</span>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as '' | NoticeStatusLabel);
              setPage(1);
            }}
            className="w-[200px] px-2.5 py-2 bg-panel-2 border border-line rounded text-[14px] text-text"
          >
            <option value="">전체</option>
            <option value="승인 완료">승인 완료</option>
            <option value="승인 거부">승인 거부</option>
            <option value="오류">오류</option>
          </select>
        </label>
      </div>

      <div className="flex items-center justify-between mb-3">
        <span className="text-[14px] text-text-dim">총 {rows.length}건</span>
        <SearchInput
          value={query}
          onChange={(v) => {
            setQuery(v);
            setPage(1);
          }}
          placeholder="스페이스, 공지 제목 검색"
          className="w-[280px]"
        />
      </div>
      {err && <p className="font-mono text-[12px] text-red mb-3">{err}</p>}

      <div className="border border-line rounded-md overflow-x-auto">
        <table className="w-full text-[14px]">
          <thead>
            <tr className="bg-panel-2 text-text-dim text-left">
              <th className="px-3 py-2.5 font-medium w-[52px]">No.</th>
              <th className="px-3 py-2.5 font-medium">스페이스</th>
              <th className="px-3 py-2.5 font-medium">공지 제목</th>
              <th className="px-3 py-2.5 font-medium whitespace-nowrap">
                활성 여부 <InfoTooltip text={ACTIVE_TOOLTIP} />
              </th>
              <th className="px-3 py-2.5 font-medium whitespace-nowrap">생성 시각</th>
              <th className="px-3 py-2.5 font-medium">상태</th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {paged.map((m, idx) => {
              const status = statusOf(m);
              const active = m.noticeStatus === 'confirmed';
              const approved = status === '승인 완료';
              const busy = busyKey === keyOf(m);
              return (
                <tr key={keyOf(m)} className="border-t border-line hover:bg-panel-hover">
                  <td className="px-3 py-2.5 text-text-dim">
                    {(safePage - 1) * PAGE_SIZE + idx + 1}
                  </td>
                  <td className="px-3 py-2.5 max-w-[140px] truncate">{labelFor(m.integrationId)}</td>
                  <td className="px-3 py-2.5 max-w-[380px]">
                    <button
                      type="button"
                      onClick={() => setDetailTarget(m)}
                      className="text-accent hover:underline truncate block max-w-full text-left"
                      title={titleOf(m)}
                    >
                      {titleOf(m)}
                    </button>
                  </td>
                  <td className="px-3 py-2.5">
                    {/* 활성 토글 — 승인 완료 상태만 조작 가능 */}
                    <button
                      type="button"
                      role="switch"
                      aria-checked={active}
                      disabled={!approved || busy}
                      onClick={() => void toggleActive(m)}
                      className={cn(
                        'relative h-5 w-9 rounded-full transition-colors',
                        active ? 'bg-accent' : 'bg-text-faint',
                        (!approved || busy) && 'opacity-40 cursor-not-allowed',
                      )}
                    >
                      <span
                        className={cn(
                          'absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-all',
                          active ? 'left-[18px]' : 'left-0.5',
                        )}
                      />
                    </button>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-text-dim">
                    {formatDateTime(m.messageCreateTime ?? m.ingestedAt)}
                  </td>
                  <td className="px-3 py-2.5">
                    <span
                      className={cn(
                        'inline-block rounded px-2 py-0.5 text-[12px] font-medium',
                        STATUS_BADGE[status],
                      )}
                    >
                      {status}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-right">
                    <button
                      type="button"
                      disabled={approved || busy}
                      onClick={() => void resend(m)}
                      className={cn(btnCls, 'mr-1.5')}
                    >
                      재전송
                    </button>
                    <button
                      type="button"
                      disabled={active || busy}
                      onClick={() => setDeleteTarget(m)}
                      className={btnCls}
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              );
            })}
            {paged.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center text-text-dim">
                  조회된 공지사항이 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className="flex justify-end items-center gap-1 mt-3">
          {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              type="button"
              disabled={n === safePage}
              onClick={() => setPage(n)}
              className={cn(
                'min-w-[28px] h-[28px] px-1 text-[13px] rounded border',
                n === safePage
                  ? 'border-accent text-accent font-semibold'
                  : 'border-line text-text-dim hover:text-text hover:border-line-strong',
              )}
            >
              {n}
            </button>
          ))}
        </div>
      )}

      {detailTarget && (
        <NoticeDetailModal
          message={detailTarget}
          status={statusOf(detailTarget)}
          onClose={() => setDetailTarget(null)}
        />
      )}
      {deleteTarget && (
        <ConfirmModal
          title="알림"
          message={'삭제한 내용은 복구할 수 없습니다.\n삭제하시겠습니까?'}
          confirmLabel="확인"
          danger={false}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={async () => {
            await setNoticeStatus(deleteTarget, 'deleted');
            toast('삭제되었습니다.', 'success');
            await load();
          }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// 공지사항 상세 팝업 (Figma Popup_공지사항 상세)
// -----------------------------------------------------------------------------
function NoticeDetailModal({
  message: m,
  status,
  onClose,
}: {
  message: ChatRecentMessage;
  status: NoticeStatusLabel;
  onClose: () => void;
}) {
  // 승인 일시 — 초안 세트의 updatedAt(승인/상태 변경 시 갱신). 조회 실패 시 '-'.
  const [approvedAt, setApprovedAt] = useState<string | null>(null);
  useEffect(() => {
    fetch(
      `${API_BASE}/api/integrations/chat/draft-sets/by-source?integrationId=${encodeURIComponent(m.integrationId)}&messageId=${encodeURIComponent(m.messageId)}`,
    )
      .then((r) => (r.ok ? (r.json() as Promise<NoticeDraftSet>) : null))
      .then((set) => setApprovedAt(set?.updatedAt ?? null))
      .catch(() => undefined);
  }, [m.integrationId, m.messageId]);

  const parsed = m.parsed;
  const section = 'text-[13px] font-semibold text-text mb-1';
  const body = 'text-[14px] text-text-dim leading-relaxed whitespace-pre-line';

  return (
    <ModalShell title="공지사항" onClose={onClose}>
      <div className="flex flex-col gap-4 max-h-[70vh] overflow-y-auto pr-1">
        <div>
          <div className={section}>제목</div>
          <div className={body}>{parsed?.header ?? m.messageText.split('\n')[0] ?? '-'}</div>
        </div>
        {parsed?.subtitle && (
          <div>
            <div className={section}>소제목</div>
            <div className={body}>{parsed.subtitle}</div>
          </div>
        )}
        {parsed && (parsed.metaData.date || parsed.metaData.time || parsed.metaData.location) && (
          <div>
            <div className={section}>정보</div>
            <div className={body}>
              {parsed.metaData.date && <div>날짜&nbsp;&nbsp;{parsed.metaData.date}</div>}
              {parsed.metaData.time && <div>시간&nbsp;&nbsp;{parsed.metaData.time}</div>}
              {parsed.metaData.location && <div>장소&nbsp;&nbsp;{parsed.metaData.location}</div>}
            </div>
          </div>
        )}
        <div>
          <div className={section}>내용</div>
          <div className={body}>{parsed?.content ?? m.messageText}</div>
        </div>
        <hr className="border-line" />
        <div>
          <div className={section}>최초 생성일</div>
          <div className={body}>{formatDateTime(m.messageCreateTime ?? m.ingestedAt)}</div>
        </div>
        <div>
          <div className={section}>승인 정보</div>
          <div className={body}>
            {status} / {approvedAt ? formatDateTime(approvedAt) : '-'}
          </div>
        </div>
      </div>
    </ModalShell>
  );
}
